import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Card } from '../../components/ui/Card';
import {
  GraduationCap,
  BookOpen,
  Calculator,
  PieChart,
  TrendingUp,
  Lightbulb,
  Gamepad2
} from 'lucide-react';

const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": "Learn to Invest: From Financial Concepts to Confident Decisions",
  "description": "Understand key investing concepts like P/E ratios, market cap, and dividends, then put that knowledge into practice with interactive stock puzzles.",
  "url": "https://wallstreetle.com/blog/learn-to-invest",
  "datePublished": "2026-03-20",
  "dateModified": "2026-03-20",
  "author": { "@type": "Organization", "name": "Wallstreetle", "url": "https://wallstreetle.com" },
  "publisher": { "@type": "Organization", "name": "Wallstreetle", "url": "https://wallstreetle.com" },
  "image": "https://wallstreetle.com/assets/share-link-image.png"
};

export default function LearnToInvestPage() {
  return (
    <>
      <Helmet>
        <title>Wallstreetle | Learn to Invest</title>
        <meta
          name="description"
          content="Learn to invest by understanding P/E ratios, market cap, dividends, and more. Practice with Wallstreetle's free interactive stock puzzles."
        />
        <meta
          name="keywords"
          content="learn to invest, investing education, financial literacy, P/E ratio explained, market cap, dividends, stock market basics, learn stocks, investing for beginners, financial education"
        />
        <link rel="canonical" href="https://wallstreetle.com/blog/learn-to-invest" />

        <meta property="og:title" content="Wallstreetle | Learn to Invest" />
        <meta property="og:description" content="Understand P/E ratios, market cap, and dividends, then practice with free interactive stock puzzles." />
        <meta property="og:url" content="https://wallstreetle.com/blog/learn-to-invest" />
        <meta property="og:type" content="article" />
        <meta property="og:image" content="https://wallstreetle.com/assets/share-link-image.png" />

        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:title" content="Wallstreetle | Learn to Invest" />
        <meta property="twitter:description" content="Understand key investing concepts and practice with free interactive stock puzzles." />
        <meta property="twitter:image" content="https://wallstreetle.com/assets/share-link-image.png" />

        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      </Helmet>

      <div className="max-w-5xl mx-auto py-12 px-4">
        {/* Hero */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 px-4 py-1.5 rounded-full text-sm font-medium mb-4">
            <GraduationCap className="w-4 h-4" /> Education
          </div>
          <h1 className="text-4xl sm:text-5xl font-serif font-bold text-foreground mb-4">
            Learn to Invest
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Financial literacy is one of the most valuable skills you can develop. Here's how to build it, concept by concept.
          </p>
        </div>

        {/* Section: Why Financial Literacy Matters */}
        <section className="mb-14">
          <h2 className="text-2xl font-serif font-bold text-foreground mb-4">Why financial literacy matters</h2>
          <div className="prose dark:prose-invert max-w-none text-muted-foreground space-y-4">
            <p>
              According to the FINRA Foundation, only 34% of Americans can answer basic financial literacy questions correctly. This knowledge gap costs real money: missed opportunities, poor investment decisions, and unnecessary fees.
            </p>
            <p>
              The good news: investing isn't rocket science. A handful of core concepts covers most of what you need to make sound decisions with your money.
            </p>
          </div>
        </section>

        {/* Section: Key Concepts */}
        <section className="mb-14">
          <h2 className="text-2xl font-serif font-bold text-foreground mb-6">Key concepts every investor should know</h2>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { title: 'P/E Ratio', desc: 'Price-to-earnings ratio tells you how much investors pay per dollar of profit. Lower P/E can signal a bargain; higher P/E may mean high growth expectations.', icon: Calculator, color: 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400' },
              { title: 'Market Capitalization', desc: 'Share price times shares outstanding. Tells you the total value of a company: large-cap ($10B+), mid-cap, or small-cap.', icon: PieChart, color: 'bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400' },
              { title: 'Dividends', desc: 'Cash payments companies make to shareholders. Dividend yield = annual dividend / share price. Great for income-focused investing.', icon: TrendingUp, color: 'bg-green-100 dark:bg-green-900/40 text-green-600 dark:text-green-400' },
              { title: 'Revenue & Earnings', desc: 'Revenue is total sales; earnings (net income) is what remains after expenses. Growing earnings generally drive stock prices higher.', icon: BookOpen, color: 'bg-orange-100 dark:bg-orange-900/40 text-orange-600 dark:text-orange-400' },
              { title: 'Debt-to-Equity', desc: 'Measures how much debt a company uses vs. shareholder equity. High leverage amplifies both gains and losses.', icon: Lightbulb, color: 'bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400' },
              { title: 'Profit Margins', desc: 'Net income / revenue. Higher margins mean a company keeps more of every dollar it earns, a sign of competitive advantage.', icon: Calculator, color: 'bg-teal-100 dark:bg-teal-900/40 text-teal-600 dark:text-teal-400' },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <Card key={item.title} className="p-5 hover:shadow-lg transition-shadow">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${item.color}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-semibold text-foreground mb-1">{item.title}</h3>
                  <p className="text-sm text-muted-foreground">{item.desc}</p>
                </Card>
              );
            })}
          </div>
        </section>

        {/* Section: Learning Approaches */}
        <section className="mb-14">
          <h2 className="text-2xl font-serif font-bold text-foreground mb-4">Books vs. practice: which is better?</h2>
          <div className="prose dark:prose-invert max-w-none text-muted-foreground space-y-4">
            <p>
              Classic books like <em>The Intelligent Investor</em> and <em>A Random Walk Down Wall Street</em> provide excellent foundations. But research on learning shows that <strong className="text-foreground">active recall and spaced repetition</strong> beat passive reading for long-term retention.
            </p>
            <p>
              The most effective approach combines both: read to understand the theory, then practice regularly to lock in the knowledge. That's exactly the gap Wallstreetle fills.
            </p>
          </div>
        </section>

        {/* Section: How Wallstreetle Makes Learning Interactive */}
        <section className="mb-14">
          <h2 className="text-2xl font-serif font-bold text-foreground mb-4">How Wallstreetle makes learning interactive</h2>
          <Card className="p-6 sm:p-8 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/30 dark:to-emerald-950/30 border-green-200 dark:border-green-800">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-green-100 dark:bg-green-900/50 rounded-lg shrink-0">
                <Gamepad2 className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
              <div className="space-y-3 text-muted-foreground">
                <p>
                  Every day, Wallstreetle presents a mystery stock across 9 categories. You see the price chart first, then unlock financial metrics tier by tier: revenue, profit margins, P/E ratio, debt levels, and more.
                </p>
                <p>
                  By the time you solve (or give up on) the puzzle, you've actively engaged with the same metrics professional analysts use. Over weeks, <strong className="text-foreground">you'll start recognizing patterns</strong>: high margins in tech, stable revenue in consumer goods, volatile earnings in energy.
                </p>
              </div>
            </div>
          </Card>
        </section>

        {/* CTA */}
        <Card className="bg-gradient-to-r from-primary to-primary/80 text-white p-8 sm:p-10 text-center">
          <h2 className="text-2xl sm:text-3xl font-serif font-bold mb-3">
            Turn knowledge into instinct
          </h2>
          <p className="text-white/90 mb-6 max-w-xl mx-auto">
            Play a free daily stock puzzle and practice reading financial data the way real investors do.
          </p>
          <Link
            to="/"
            className="inline-block bg-white text-primary font-semibold px-8 py-3 rounded-lg hover:bg-white/90 transition-colors"
          >
            Start Learning Free
          </Link>
        </Card>
      </div>
    </>
  );
}
